import React, { useMemo } from 'react'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import OfferInclusionItem from 'components/Common/Inclusions/OfferInclusionItem'
import CruiseLuxPlusInclusionList from './CruiseLuxPlusInclusionList'

import { buildAllStandardInclusions } from 'lib/cruises/cruiseInclusionsUtils'
import styled from 'styled-components'
import { rem } from 'polished'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyExperiments } from 'constants/optimizely'
import { getOptimizelyEnabledDecision } from 'lib/optimizely/optimizelyUtils'
import LabeledSheet from 'LuxKit/LabeledSheet'
import PromotionLabel from 'LuxKit/Label/PromotionLabel'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'

const PromotionListWrapper = styled.div`
  padding: ${rem(4)} 0;
`

// Same styling as LuxPlus inclusions
const InclusionList = styled(VerticalSpacer)`
  padding: ${rem(8)};
`

interface Props {
  offer: App.CruiseOffer
  evergreenInclusions?: Array<App.EvergreenInclusion>
  tileInclusions?: Array<App.OfferInclusion>
  standardInclusions: Array<App.PackageInclusion>
  luxPlusInclusions: Array<App.PackageInclusion>
  gap?: React.ComponentProps<typeof Group>['gap']
}

function CruiseInclusionList({ offer, standardInclusions, luxPlusInclusions, evergreenInclusions, tileInclusions, gap = 8 }: Props) {
  // TODO: Remove this once the A/B test is complete https://aussiecommerce.atlassian.net/browse/CRUZ-3360
  const cruiseSearchTileRedesignEnabled = getOptimizelyEnabledDecision(useOptimizelyExperiment(OptimizelyExperiments.cruiseSearchTileRedesign))

  const allStandardInclusions = useMemo(() => {
    return buildAllStandardInclusions(evergreenInclusions, standardInclusions, tileInclusions)
  }, [evergreenInclusions, standardInclusions, tileInclusions])

  const hasStandardInclusions = !!allStandardInclusions.length
  const hasInclusions = hasStandardInclusions || !!luxPlusInclusions?.length

  // Control cruiseSearchTileRedesignEnabled: Show existing layout
  if (!cruiseSearchTileRedesignEnabled) {
    return <>
      {hasInclusions && <PromotionListWrapper>
        <Group direction="vertical" gap={gap} horizontalAlign="start">
          <CruiseLuxPlusInclusionList luxPlusInclusions={luxPlusInclusions} offer={offer} />

          {hasStandardInclusions && <Group direction="vertical" gap={4} horizontalAlign="start">
            <BodyText weight="bold" variant="medium">
              <b>Highlights</b> (select packages only)
            </BodyText>

            <Group direction="vertical" gap={4}>
              {allStandardInclusions.map((inclusion) => <OfferInclusionItem
                key={inclusion.id}
                inclusion={inclusion}
                colour={inclusion.isHighlighted ? 'highlight-secondary' : undefined}
                showMinStayDescription
              />)}
            </Group>
          </Group>}
        </Group>
      </PromotionListWrapper>}
    </>
  }

  // Variant cruiseSearchTileRedesignEnabled: Show special offer badge with inclusions like LuxPlus
  return <>
    {hasInclusions && <Group direction="vertical" gap={8} horizontalAlign="start">
      <CruiseLuxPlusInclusionList luxPlusInclusions={luxPlusInclusions} offer={offer} />

      {hasStandardInclusions && <LabeledSheet
        kind="highlight"
        label={<PromotionLabel icon={<SolidFireIcon />} variant="premium">Special offer</PromotionLabel>}
      >
        <InclusionList gap={4}>
          {allStandardInclusions.map((inclusion) => <OfferInclusionItem
            key={inclusion.id}
            inclusion={inclusion}
            colour={inclusion.isHighlighted ? 'highlight-secondary' : undefined}
            showMinStayDescription
          />)}
          <BodyText variant="small" colour="neutral-one">
            Available on select packages
          </BodyText>
        </InclusionList>
      </LabeledSheet>}
    </Group>}
  </>
}

export default CruiseInclusionList
